import type { PermissionState, PluginListenerHandle } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import type { PushProvider, StreamChat } from "stream-chat";
import { getPlatform } from "Utils/Platform/PlatformUtils";
import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from "Utils/Storage/LocalStorage/SecureLocalStorage";

/**
 * Chat push on the native shells (iOS via APNs, Android via FCM). Stream sends the push itself;
 * all this device has to do is hand its push token to Stream under the ACTIVE persona's chat user
 * (`client.addDevice`). The token follows whichever persona is connected: a persona switch removes
 * it from the previous persona (streamChatClient) before adding it to the next, and logout removes
 * it for every persona server-side — other personas of the same person do not push to this phone
 * (explicit v1 deferral).
 *
 * Everything here is native-shell only; callers guard with `isNativePlatform()`. The Capacitor
 * plugin proxies throw "not implemented" on plain web, so nothing below may run there.
 */

export type ChatPushPreference = "on" | "off";

/**
 * Names of the push providers in the Stream dashboard — one per platform. Stream requires the name
 * when the app has a named/multi-bundle provider, and silently registers against nothing when it
 * does not match: the dashboard providers MUST be created under exactly these names.
 */
export const STREAM_APN_PROVIDER_NAME = "MooneAPN";
export const STREAM_FCM_PROVIDER_NAME = "MooneFCM";

type ChatPushProvider = {
  type: PushProvider;
  name: string;
};

const CHAT_PUSH_PROVIDER_BY_PLATFORM: Record<
  "ios" | "android",
  ChatPushProvider
> = {
  ios: { type: "apn", name: STREAM_APN_PROVIDER_NAME },
  android: { type: "firebase", name: STREAM_FCM_PROVIDER_NAME },
};

const getChatPushProvider = (): ChatPushProvider => {
  const platform = getPlatform();
  if (platform !== "ios" && platform !== "android") {
    throw new Error(
      `Chat push has no Stream provider for platform "${platform}"`,
    );
  }
  return CHAT_PUSH_PROVIDER_BY_PLATFORM[platform];
};

/** How long to wait for the OS to hand back a push token before giving up on this launch. */
const REGISTRATION_TIMEOUT_MS = 30_000;

/**
 * APNs/FCM never answered within the timeout — an offline/flaky-network launch, not a code
 * defect. Callers report it at a lower severity than a real registration failure.
 */
export class PushRegistrationTimeoutError extends Error {
  constructor() {
    super("Push token registration timed out");
    this.name = "PushRegistrationTimeoutError";
  }
}

/** null = never asked: the chat gate prompts on first entry. */
export const getChatPushPreference = (): ChatPushPreference | null => {
  const value = getLocalStorageItem(LocalStorageKeys.ChatPushPreference);
  return value === "on" || value === "off" ? value : null;
};

export const setChatPushPreference = (preference: ChatPushPreference) =>
  setLocalStorageItem(LocalStorageKeys.ChatPushPreference, preference);

export const getStoredDeviceToken = (): string | null =>
  getLocalStorageItem(LocalStorageKeys.ChatPushDeviceToken);

export const getChatPushPermission = async (): Promise<PermissionState> =>
  (await PushNotifications.checkPermissions()).receive;

/**
 * Shows the system prompt only while the OS still allows one; a settled state (granted/denied)
 * is returned as-is because neither iOS nor Android re-prompts — the user has to go to the
 * system settings.
 */
export const requestChatPushPermission = async (): Promise<PermissionState> => {
  const current = await getChatPushPermission();
  if (current !== "prompt" && current !== "prompt-with-rationale") {
    return current;
  }
  return (await PushNotifications.requestPermissions()).receive;
};

/**
 * Asks the OS for the push token (APNs on iOS, FCM on Android — the plugin surfaces both through
 * the same `registration` event). Listeners are attached BEFORE `register()` because the plugin
 * fires `registration` as soon as the OS answers, and the token is stored so logout can remove
 * the device from Stream without asking the OS again. Push tokens can rotate, so this runs on
 * every launch, not only the first.
 */
const registerDeviceToken = async (): Promise<string> => {
  const handles: Array<Promise<PluginListenerHandle>> = [];
  let timeout: number | undefined;
  const cleanUp = () => {
    window.clearTimeout(timeout);
    for (const handle of handles) {
      void handle.then((h) => h.remove());
    }
  };

  const tokenPromise = new Promise<string>((resolve, reject) => {
    timeout = window.setTimeout(() => {
      cleanUp();
      reject(new PushRegistrationTimeoutError());
    }, REGISTRATION_TIMEOUT_MS);

    handles.push(
      PushNotifications.addListener("registration", (token) => {
        cleanUp();
        resolve(token.value);
      }),
      PushNotifications.addListener("registrationError", (error) => {
        cleanUp();
        reject(new Error(`Push token registration failed: ${error.error}`));
      }),
    );
  });

  try {
    await PushNotifications.register();
  } catch (error) {
    // Nobody awaits `tokenPromise` past this point: without the cleanup its timeout would still
    // fire and reject it into an unhandled rejection, with the listeners attached until then.
    cleanUp();
    throw error;
  }

  const token = await tokenPromise;
  setLocalStorageItem(LocalStorageKeys.ChatPushDeviceToken, token);
  return token;
};

/**
 * Registers this device with Stream for the persona `client` is connected as. Returns false when
 * the OS permission is not granted (nothing registered). Throws on plugin/Stream failures — the
 * caller decides whether that is reportable.
 */
export const registerChatPushDevice = async (
  client: StreamChat,
): Promise<boolean> => {
  if ((await getChatPushPermission()) !== "granted") {
    return false;
  }
  const provider = getChatPushProvider();
  const token = await registerDeviceToken();
  await client.addDevice(token, provider.type, undefined, provider.name);
  return true;
};

/**
 * Best-effort: removes the stored token from the connected persona's Stream devices. A device
 * Stream no longer knows is not an error worth surfacing.
 */
export const removeChatPushDevice = async (
  client: StreamChat,
): Promise<void> => {
  const token = getStoredDeviceToken();
  if (!token) {
    return;
  }
  await client.removeDevice(token).catch(() => undefined);
};
